import { RoofType, roofFieldName } from 'src/store/roofData/types';

interface LineCoord {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  labelT?: number;
}

type CoordsMap = { [field in roofFieldName]?: LineCoord };

export const dimensionCoordinates: { [type in RoofType]: CoordsMap } = {
  gambrel: {
    L_kr: { x1: 640, y1: 580, x2: 1752, y2: 553 },
    B_kr1: { x1: 645, y1: 570, x2: 604, y2: 390 },
    B_kr2: { x1: 358, y1: 545, x2: 380, y2: 410 },
    H_p1: { x1: 470, y1: 182, x2: 370, y2: 400, labelT: 0.7 },
    H_p2: { x1: 470, y1: 182, x2: 600, y2: 380, labelT: 0.7 },
  },
  hip: {
    L_kr: { x1: 645, y1: 555, x2: 1762, y2: 535 },
    B_kr: { x1: 363, y1: 524, x2: 645, y2: 556 },
    H_p1: { x1: 760, y1: 230, x2: 965, y2: 540, labelT: 0.7 },
    H_p2: { x1: 750, y1: 230, x2: 490, y2: 530, labelT: 0.7 },
    L_k: { x1: 750, y1: 230, x2: 1250, y2: 247, labelT: 0.7 },
  },
  tented: {
    L_kr: { x1: 630, y1: 556, x2: 1749, y2: 533 },
    B_kr: { x1: 351, y1: 527, x2: 628, y2: 556 },
    H_p1: { x1: 1005, y1: 235, x2: 1245, y2: 530, labelT: 0.7 },
    H_p2: { x1: 1005, y1: 235, x2: 490, y2: 530, labelT: 0.7 },
  },
  multipitch: {
    L_kr: { x1: 630, y1: 593, x2: 1744, y2: 562, labelT: 0.35 },
    L_k: { x1: 965, y1: 280, x2: 1260, y2: 232 },
    B_kr: { x1: 315, y1: 543, x2: 628, y2: 595 },
    B_kr2: { x1: 630, y1: 593, x2: 1744, y2: 562, labelT: 0.7 },
    H_p1: { x1: 430, y1: 260, x2: 430, y2: 570 },
  },
  gable: {
    L_kr: { x1: 625, y1: 560, x2: 1748, y2: 540 },
    L_sk1: { x1: 460, y1: 240, x2: 625, y2: 558, labelT: 0.7 },
    L_sk2: { x1: 460, y1: 240, x2: 340, y2: 538, labelT: 0.7 },
  },
};

const roundRect = (
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number,
) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
};

const formatValue = (v: number | null | undefined): string => {
  if (v === null || v === undefined) return '';
  return Number.isInteger(v) ? `${v}` : `${v}`;
};

export const drawSingleDimension = (
  canvas: HTMLCanvasElement,
  roofType: RoofType,
  field: roofFieldName,
) => {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  const c = dimensionCoordinates[roofType]?.[field];
  if (!c) return;
  ctx.beginPath();
  ctx.lineWidth = 10;
  ctx.strokeStyle = '#EE6131';
  ctx.moveTo(c.x1, c.y1);
  ctx.lineTo(c.x2, c.y2);
  ctx.stroke();
  ctx.closePath();
};

export const drawAllDimensions = (
  canvas: HTMLCanvasElement,
  roofType: RoofType,
  values: { [k: string]: number | null | undefined },
) => {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  const coords = dimensionCoordinates[roofType];
  if (!coords) return;

  (Object.keys(coords) as roofFieldName[]).forEach((field) => {
    const c = coords[field];
    if (!c) return;

    ctx.beginPath();
    ctx.lineWidth = 6;
    ctx.strokeStyle = '#EE6131';
    ctx.moveTo(c.x1, c.y1);
    ctx.lineTo(c.x2, c.y2);
    ctx.stroke();
    ctx.closePath();
  });

  ctx.font = 'bold 32px Montserrat, Arial, sans-serif';
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'center';

  (Object.keys(coords) as roofFieldName[]).forEach((field) => {
    const c = coords[field];
    if (!c) return;

    const val = values[`${field}Value`];
    const valStr = formatValue(val);
    if (!valStr) return;
    const text = `${valStr} м`;

    const t = c.labelT ?? 0.5;
    const mx = c.x1 + (c.x2 - c.x1) * t;
    const my = c.y1 + (c.y2 - c.y1) * t;
    const metrics = ctx.measureText(text);
    const tw = metrics.width;
    const th = 36;
    const padX = 12;
    const padY = 6;

    ctx.fillStyle = '#FFFFFF';
    roundRect(ctx, mx - tw / 2 - padX, my - th / 2 - padY, tw + 2 * padX, th + 2 * padY, 6);
    ctx.fill();
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#EE6131';
    ctx.stroke();

    ctx.fillStyle = '#000000';
    ctx.fillText(text, mx, my);
  });
};
